<template>
  <main class="page-shell">
    <a class="skip-link" href="#main-content">Skip to main content</a>

    <header class="site-header">
      <div class="container nav-wrap">
        <router-link to="/" class="brand">Jerald Pamugas</router-link>
        <nav aria-label="Primary navigation" class="desktop-nav">
          <a v-for="item in navLinks" :key="item.id" :href="`#${item.id}`">{{ item.label }}</a>
        </nav>
        <a :href="profile.scheduleCallUrl" class="schedule-btn" target="_blank" rel="noopener noreferrer">
          Schedule a Call
        </a>
      </div>
    </header>

    <div id="main-content" class="container">
      <section class="hero section fade-up">
        <div class="hero-content surface">
          <p class="eyebrow">Enterprise Software Delivery</p>
          <h1>{{ profile.title }}</h1>
          <p class="subheadline">{{ profile.subheadline }}</p>

          <div class="availability" aria-label="Availability status">
            <span class="status-dot" aria-hidden="true"></span>
            {{ profile.availability }}
          </div>

          <div class="hero-actions">
            <a href="#contact" class="btn-primary">Work With Me</a>
            <a href="#case-studies" class="btn-secondary">View Case Studies</a>
          </div>

          <p class="trust-line">
            Trusted in enterprise environments to own delivery across discovery, architecture, implementation, and support.
          </p>

          <ul class="hero-meta" aria-label="Professional overview">
            <li><strong>{{ profile.experienceYears }}</strong> experience</li>
            <li><strong>{{ profile.currentCompany }}</strong> current company</li>
            <li><strong>{{ profile.location }}</strong> base location</li>
            <li><strong>{{ profile.rate }}</strong> target contract range</li>
          </ul>
        </div>
      </section>

      <section id="why-work-with-me" class="section">
        <SectionHeading
          eyebrow="Why Work With Me"
          title="Built for teams that value ownership, clarity, and long-term outcomes."
          description="Every engagement is focused on business impact and dependable delivery, not just shipping features."
        />
        <div class="grid cards-grid">
          <article v-for="item in differentiators" :key="item.title" class="surface info-card fade-up">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </article>
        </div>
      </section>

      <section id="core-expertise" class="section">
        <SectionHeading
          eyebrow="Core Expertise"
          title="Depth where enterprise teams need it most."
          description="Focused technology groups, based on production experience."
        />
        <div class="grid expertise-grid">
          <article v-for="group in expertiseGroups" :key="group.title" class="surface expertise-card fade-up">
            <h3>{{ group.title }}</h3>
            <ul>
              <li v-for="tool in group.items" :key="tool">{{ tool }}</li>
            </ul>
          </article>
        </div>
      </section>

      <section id="case-studies" class="section">
        <SectionHeading
          eyebrow="Featured Case Studies"
          title="Selected work focused on measurable business outcomes."
          description="Each case study outlines the problem, solution approach, technology stack, and impact."
        />
        <div class="grid cards-grid">
          <CaseStudyCard v-for="study in caseStudies" :key="study.slug" :study="study" class="fade-up" />
        </div>
      </section>

      <section id="experience" class="section">
        <SectionHeading
          eyebrow="Professional Experience"
          title="Enterprise delivery experience across modernization and automation."
        />
        <div class="grid experience-grid">
          <article v-for="item in professionalExperience" :key="item.company" class="surface experience-card fade-up">
            <div class="experience-head">
              <h3>{{ item.company }}</h3>
              <p>{{ item.role }} · {{ item.period }}</p>
            </div>
            <ul>
              <li v-for="point in item.highlights" :key="point">{{ point }}</li>
            </ul>
          </article>
        </div>
      </section>

      <section id="process" class="section">
        <SectionHeading
          eyebrow="Development Process"
          title="A structured delivery model from discovery to long-term support."
        />
        <ol class="surface process-list fade-up" aria-label="Development process steps">
          <li v-for="step in processSteps" :key="step">
            <span>{{ step }}</span>
          </li>
        </ol>
      </section>

      <section id="services" class="section">
        <SectionHeading
          eyebrow="Services"
          title="What teams hire me for."
          description="Service areas focused on delivery outcomes, operational reliability, and long-term maintainability."
        />
        <div class="grid services-grid">
          <article v-for="service in services" :key="service.title" class="surface service-card fade-up">
            <h3>{{ service.title }}</h3>
            <p>{{ service.description }}</p>
          </article>
        </div>
      </section>

      <section id="currently-learning" class="section">
        <SectionHeading
          eyebrow="Currently Learning"
          title="Continuous learning to support modern architecture decisions."
          description="Current focus areas that strengthen delivery for modern cloud-native and enterprise systems."
        />
        <div class="surface learning-card fade-up">
          <ul>
            <li v-for="item in currentlyLearning" :key="item">{{ item }}</li>
          </ul>
        </div>
      </section>

      <section id="testimonials" class="section">
        <SectionHeading
          eyebrow="Testimonials"
          title="Recommendations section in progress."
          description="LinkedIn recommendations will be added here."
        />
        <div class="grid cards-grid">
          <article v-for="item in testimonials" :key="item.name" class="surface testimonial-card fade-up">
            <p class="quote">"{{ item.quote }}"</p>
            <p class="author">{{ item.name }} · {{ item.role }}</p>
          </article>
        </div>
      </section>

      <section id="faq" class="section">
        <SectionHeading
          eyebrow="FAQ"
          title="Common questions from hiring managers and founders."
        />
        <div class="surface faq-wrapper fade-up">
          <details v-for="item in faqItems" :key="item.question">
            <summary>{{ item.question }}</summary>
            <p>{{ item.answer }}</p>
          </details>
        </div>
      </section>

      <section id="contact" class="section">
        <SectionHeading
          eyebrow="Contact"
          title="Open to freelance, contract, and remote opportunities."
          description="If you are planning a long-term product build or enterprise software initiative, let us discuss scope, timeline, and delivery model."
        />
        <div class="surface contact-card fade-up">
          <div class="contact-grid">
            <a :href="`mailto:${profile.email}`">Email: {{ profile.email }}</a>
            <a :href="profile.github" target="_blank" rel="noopener noreferrer">GitHub</a>
            <a :href="profile.linkedin" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <a :href="profile.resumeUrl" target="_blank" rel="noopener noreferrer">Resume Download</a>
            <p>Availability: {{ profile.availability }}</p>
          </div>
          <div class="contact-actions">
            <a :href="profile.scheduleCallUrl" class="btn-primary" target="_blank" rel="noopener noreferrer">
              Let's Build Together
            </a>
            <a :href="`mailto:${profile.email}`" class="btn-secondary">Discuss Your Project</a>
            <router-link to="/blog" class="btn-secondary">Engineering Notes</router-link>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import SectionHeading from "@/components/site/SectionHeading.vue";
import CaseStudyCard from "@/components/site/CaseStudyCard.vue";
import {
  profile,
  navLinks,
  differentiators,
  expertiseGroups,
  caseStudies,
  professionalExperience,
  processSteps,
  services,
  currentlyLearning,
  faqItems,
  testimonials
} from "@/data/portfolioContent";

export default {
  name: "Guestpage",
  components: {
    SectionHeading,
    CaseStudyCard
  },
  data() {
    return {
      profile,
      navLinks,
      differentiators,
      expertiseGroups,
      caseStudies,
      professionalExperience,
      processSteps,
      services,
      currentlyLearning,
      faqItems,
      testimonials
    };
  },
  mounted() {
    document.title = "Jerald Pamugas | Senior Full Stack Developer";
  }
};
</script>

<style scoped>
.skip-link {
  position: absolute;
  left: -9999px;
}

.skip-link:focus {
  left: 1rem;
  top: 1rem;
  background: #fff;
  color: #000;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  z-index: 30;
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 20;
  backdrop-filter: blur(10px);
  background: rgba(5, 8, 14, 0.64);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.nav-wrap {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.brand {
  font-family: var(--font-display);
  letter-spacing: 0.01em;
  text-decoration: none;
  font-weight: 600;
}

.desktop-nav {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
}

.desktop-nav a {
  text-decoration: none;
  color: var(--text-secondary);
  font-size: 0.93rem;
}

.desktop-nav a:hover,
.desktop-nav a:focus-visible {
  color: var(--text-primary);
}

.schedule-btn {
  text-decoration: none;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  padding: 0.5rem 0.95rem;
  font-size: 0.88rem;
}

.hero {
  padding-top: clamp(2.8rem, 6vw, 4.5rem);
}

.hero-content {
  padding: clamp(1.5rem, 4vw, 3rem);
}

.eyebrow {
  margin: 0;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.8rem;
}

h1 {
  margin: 0.8rem 0 1rem;
  font-family: var(--font-display);
  font-size: clamp(2rem, 5vw, 3.65rem);
  letter-spacing: -0.02em;
  line-height: 1.16;
}

.subheadline {
  max-width: 760px;
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.8;
}

.availability {
  margin-top: 1.3rem;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  border: 1px solid rgba(125, 211, 252, 0.3);
  color: #d6f2ff;
  border-radius: 999px;
  padding: 0.5rem 0.95rem;
  background: rgba(56, 189, 248, 0.09);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #67e8f9;
}

.hero-actions {
  margin-top: 1.4rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.btn-primary,
.btn-secondary {
  text-decoration: none;
  border-radius: 999px;
  padding: 0.78rem 1.2rem;
  font-weight: 650;
}

.btn-primary {
  background: var(--accent-strong);
  color: #071019;
}

.btn-primary:hover,
.btn-primary:focus-visible {
  background: #0ea5e9;
}

.btn-secondary {
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.hero-meta {
  margin: 1.4rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  color: var(--text-secondary);
}

.hero-meta strong {
  color: var(--text-primary);
  font-weight: 700;
}

.trust-line {
  margin: 1rem 0 0;
  color: var(--text-muted);
  line-height: 1.7;
}

.cards-grid,
.expertise-grid,
.services-grid,
.experience-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.cards-grid :deep(.case-study-card:first-child) {
  grid-column: span 2;
}

.info-card,
.expertise-card,
.service-card,
.experience-card,
.testimonial-card,
.contact-card,
.faq-wrapper,
.process-list {
  padding: 1.25rem;
}

.info-card h3,
.expertise-card h3,
.service-card h3,
.experience-card h3 {
  margin: 0;
  font-size: 1.15rem;
}

.info-card p,
.service-card p,
.experience-head p,
.quote,
.contact-grid,
.contact-grid p {
  color: var(--text-secondary);
}

.info-card p,
.service-card p,
.quote {
  margin: 0.7rem 0 0;
  line-height: 1.7;
}

.expertise-card ul,
.experience-card ul {
  margin: 0.75rem 0 0;
  padding-left: 1.15rem;
  color: var(--text-secondary);
  display: grid;
  gap: 0.45rem;
}

.experience-head p {
  margin: 0.45rem 0 0;
}

.process-list {
  list-style: none;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.6rem;
}

.process-list li {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  padding: 0.85rem 0.7rem;
  min-height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--text-secondary);
}

.testimonial-card .author {
  margin: 0.85rem 0 0;
  color: var(--text-muted);
  font-size: 0.92rem;
}

.faq-wrapper {
  display: grid;
  gap: 0.6rem;
}

details {
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  padding: 0.85rem;
  background: rgba(255, 255, 255, 0.018);
}

summary {
  cursor: pointer;
  font-weight: 650;
}

details p {
  margin: 0.6rem 0 0;
  color: var(--text-secondary);
  line-height: 1.7;
}

.contact-grid {
  display: grid;
  gap: 0.6rem;
}

.contact-grid a,
.contact-grid p {
  margin: 0;
  text-decoration: none;
}

.contact-grid a:hover,
.contact-grid a:focus-visible {
  color: var(--accent);
}

.contact-actions {
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
}

.learning-card ul {
  margin: 0;
  padding-left: 1.1rem;
  color: var(--text-secondary);
  display: grid;
  gap: 0.45rem;
}

@media (max-width: 960px) {
  .desktop-nav {
    display: none;
  }

  .cards-grid,
  .expertise-grid,
  .services-grid,
  .experience-grid {
    grid-template-columns: 1fr;
  }

  .cards-grid :deep(.case-study-card:first-child) {
    grid-column: span 1;
  }

  .process-list {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .process-list {
    grid-template-columns: 1fr;
  }

  .schedule-btn {
    display: none;
  }
}
</style>
